import React, { useState } from 'react';
import { HiOutlineCheckCircle, HiOutlineCollection, HiOutlineLink, HiOutlinePaperClip } from 'react-icons/hi';
import BadgeStatus from '../../../../../../atoms/BadgeStatus';
import SelectTextField from '../../../../../../molecules/SelectTextField';
import { ProviderBinding, ProviderTemplate, Template } from '../../../../../../../types/communication';
import { findInCatalog, providerPlaceholders } from '../../../../../../../utils/providerCatalog';
import SectionCard from '../../../shared/SectionCard';
import ProviderTemplatePicker from './ProviderTemplatePicker';
import VariablePicker from '../../../shared/VariablePicker';

interface WhatsAppContentEditorProps {
    template: Template;
    disabled: boolean;
    catalog: ProviderTemplate[];
    onChange: (patch: Partial<Template>) => void;
}

const STATUS_BADGE = {
    APPROVED: { color: 'green' as const, text: 'Aprovado' },
    PENDING: { color: 'pending' as const, text: 'Em análise' },
    REJECTED: { color: 'red' as const, text: 'Reprovado' },
};

const CATEGORY_LABEL = { UTILITY: 'Utilidade', MARKETING: 'Marketing', AUTHENTICATION: 'Autenticação' };

const contextOf = (body: string, placeholder: string): string => {
    const token = `{{${placeholder}}}`;
    const index = body.indexOf(token);
    if (index < 0) return '';
    const before = body.slice(Math.max(0, index - 28), index).replace(/\s+/g, ' ');
    const after = body.slice(index + token.length, index + token.length + 20).replace(/\s+/g, ' ');
    return `…${before}▢${after}…`;
};

const ProviderCard: React.FC<{ template: ProviderTemplate; selected: boolean; expanded?: boolean; onClick?: () => void }> = ({
    template: p,
    selected,
    expanded,
    onClick,
}) => {
    const usable = p.status === 'APPROVED';
    const body = (
        <>
            <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-gray-800 dark:text-gray-100 truncate flex-1">{p.name}</span>
                {selected && <HiOutlineCheckCircle className="text-emerald-600" size={18} />}
            </div>
            <div className="flex items-center gap-2 mt-1.5">
                <BadgeStatus color={STATUS_BADGE[p.status].color} text={STATUS_BADGE[p.status].text} />
                <span className="text-[11px] text-gray-400">{CATEGORY_LABEL[p.category]}</span>
                {p.header.type === 'DOCUMENT' && (
                    <span className="inline-flex items-center gap-0.5 text-[11px] text-gray-400">
                        <HiOutlinePaperClip size={12} /> anexo
                    </span>
                )}
            </div>
            <p className={`text-xs text-gray-500 dark:text-gray-400 mt-2 ${expanded ? 'whitespace-pre-line' : 'line-clamp-2'}`}>{p.body}</p>
        </>
    );
    const frame = `text-left rounded-xl border p-3 ${
        selected ? 'border-emerald-500 bg-emerald-50/70 dark:bg-emerald-900/20' : 'border-gray-200 dark:border-slate-700'
    }`;
    if (!onClick) return <div className={frame}>{body}</div>;
    return (
        <button
            type="button"
            disabled={!usable}
            onClick={onClick}
            title={usable ? '' : 'Só modelos aprovados podem ser usados'}
            className={`${frame} transition-colors disabled:cursor-not-allowed hover:border-gray-300 ${!usable ? 'opacity-50' : ''}`}
        >
            {body}
        </button>
    );
};

const WhatsAppContentEditor: React.FC<WhatsAppContentEditorProps> = ({ template, disabled, catalog, onChange }) => {
    const provider = findInCatalog(catalog, template.providerTemplateName);
    const [browsing, setBrowsing] = useState(false);
    const bindings = template.providerBindings ?? [];

    const choose = (name: string) => {
        const next = findInCatalog(catalog, name);
        if (!next) return;
        const previous = new Map(bindings.map((b) => [b.placeholder, b.variable]));
        const providerBindings: ProviderBinding[] = providerPlaceholders(next).map((placeholder) => ({
            placeholder,
            variable: previous.get(placeholder) ?? '',
        }));
        onChange({ providerTemplateName: name, providerBindings });
    };

    const bind = (placeholder: string, variable: string) =>
        onChange({ providerBindings: bindings.map((b) => (b.placeholder === placeholder ? { ...b, variable } : b)) });

    return (
        <div className="flex flex-col gap-4">
            <SectionCard
                title="Modelo aprovado no WhatsApp"
                description={
                    disabled
                        ? 'O texto é cadastrado e aprovado no WhatsApp.'
                        : 'O texto é cadastrado e aprovado no WhatsApp. Aqui você escolhe qual usar.'
                }
                icon={<HiOutlineCollection size={18} />}
                actions={
                    !disabled && provider && !browsing ? (
                        <button type="button" onClick={() => setBrowsing(true)} className="text-xs text-teal-700 dark:text-teal-300 hover:underline">
                            Trocar modelo
                        </button>
                    ) : undefined
                }
            >
                {provider && !browsing ? (
                    <ProviderCard template={provider} selected expanded />
                ) : disabled ? (
                    <p className="text-sm text-gray-400">Nenhum modelo escolhido.</p>
                ) : (
                    <ProviderTemplatePicker
                        selectedName={template.providerTemplateName}
                        catalog={catalog}
                        onPick={(name) => {
                            choose(name);
                            setBrowsing(false);
                        }}
                        onCancel={provider ? () => setBrowsing(false) : undefined}
                    />
                )}
            </SectionCard>

            {provider && (
                <SectionCard
                    title="Preencher os espaços do modelo"
                    description="Cada espaço numerado do WhatsApp recebe o valor de uma variável no momento do envio."
                    icon={<HiOutlineLink size={18} />}
                >
                    <div className="divide-y divide-gray-100 dark:divide-slate-700">
                        {bindings.map((binding) => (
                            <div key={binding.placeholder} className="grid grid-cols-[88px_minmax(0,1fr)_minmax(0,1fr)] items-center gap-3 py-2">
                                <span
                                    className={`inline-flex justify-center rounded-md px-2 py-1 text-xs font-mono ${
                                        binding.variable ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' : 'bg-red-50 text-red-700'
                                    }`}
                                >
                                    {binding.placeholder === 'documento' ? 'Anexo' : `{{${binding.placeholder}}}`}
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                    {binding.placeholder === 'documento' ? 'Documento enviado no topo da mensagem' : contextOf(provider.body, binding.placeholder)}
                                </span>
                                <div className="flex items-center gap-2">
                                    <SelectTextField
                                        id={`binding-${binding.placeholder}`}
                                        label="Variável"
                                        value={binding.variable}
                                        disabled={disabled}
                                        emptyLabel="Escolha a variável…"
                                        className="flex-1 min-w-0"
                                        errorMessage={!binding.variable ? 'Obrigatório' : undefined}
                                        items={template.variables.map((v) => ({ value: v.name, label: `${v.label} (${v.name})` }))}
                                        onChange={(e) => bind(binding.placeholder, e.target.value)}
                                    />
                                    {!disabled && (
                                        <VariablePicker variables={template.variables} compact onPick={(name) => bind(binding.placeholder, name)} />
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </SectionCard>
            )}
        </div>
    );
};

export default WhatsAppContentEditor;
